import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  Phone,
  ArrowLeft,
  TrendingUp,
  CheckCircle2,
  Clock,
  Lock,
  LogOut,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "meritz_admin_token";

export default function AdminDashboard() {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? sessionStorage.getItem(TOKEN_KEY) : null,
  );

  if (!token) {
    return <PasscodeGate onSuccess={(t) => setToken(t)} />;
  }

  return (
    <Dashboard
      token={token}
      onLogout={() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

function PasscodeGate({ onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 4) {
      setErr("Enter your passcode");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        passcode: code,
      });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back");
      onSuccess(data.token);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Login failed";
      setErr(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-gate"
      className="min-h-screen bg-[#1a1815] grid place-items-center px-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#F9F6F0] rounded-3xl p-8 shadow-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-full bg-[#C73E3A] text-white grid place-items-center">
            <Lock size={20} />
          </span>
          <div>
            <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
              Meritz Admin
            </p>
            <h1 className="font-display text-2xl font-extrabold">Enter passcode</h1>
          </div>
        </div>
        <label className="block mt-6">
          <span className="block text-xs font-semibold tracking-wider uppercase text-[#6B635A] mb-2">
            Passcode
          </span>
          <input
            data-testid="admin-passcode-input"
            type="password"
            inputMode="numeric"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="••••••"
            className="w-full h-12 px-4 rounded-xl border border-[#E8E2D6] bg-white font-mono tracking-[0.4em] text-center text-lg focus:outline-none focus:border-[#C73E3A]"
          />
        </label>
        {err && (
          <p data-testid="admin-error" className="mt-3 text-sm font-medium text-[#C73E3A]">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C73E3A] hover:bg-[#a52f2c] disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-colors"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Checking…" : "Unlock"}
        </button>
        <Link
          to="/"
          data-testid="admin-gate-back"
          className="mt-4 block text-center text-sm font-medium text-[#6B635A] hover:text-[#C73E3A]"
        >
          ← Back to website
        </Link>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [reservations, setReservations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    const headers = { "X-Admin-Token": token };
    Promise.all([
      axios.get(`${API}/reservations`, { headers }).then((r) => r.data),
      axios.get(`${API}/contact`, { headers }).then((r) => r.data),
      axios.get(`${API}/subscribers`, { headers }).then((r) => r.data),
    ])
      .then(([res, msgs, subs]) => {
        if (!live) return;
        setReservations(res || []);
        setContacts(msgs || []);
        setSubscribers(subs || []);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          onLogout();
        }
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [token, onLogout]);

  const today = new Date().toISOString().split("T")[0];
  const todayCount = reservations.filter((r) => r.date === today).length;
  const upcoming = reservations.filter((r) => r.date >= today).length;
  const totalGuests = reservations.reduce(
    (sum, r) => sum + (r.party_size || 0),
    0,
  );

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#F9F6F0] text-[#2C2825]"
    >
      <header className="border-b border-[#E8E2D6] bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-back"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#D9534F]"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-block w-9 h-9 rounded-full bg-[#D9534F] text-white grid place-items-center font-display font-extrabold">
                M
              </span>
              <span className="font-display text-lg font-extrabold tracking-tight">
                Dashboard
              </span>
            </div>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#6B635A] hover:text-[#C73E3A] border border-[#E8E2D6] hover:border-[#C73E3A] px-3 py-1.5 rounded-full transition-colors"
            >
              <LogOut size={13} /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
              Meritz Operations
            </p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              Today at a glance
            </h1>
          </div>
          <p className="text-sm text-[#6B635A]">
            {new Date().toLocaleDateString("en-NG", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid="stat-today"
            icon={<Calendar size={18} />}
            label="Today's bookings"
            value={todayCount}
            tone="primary"
          />
          <StatCard
            testid="stat-upcoming"
            icon={<TrendingUp size={18} />}
            label="Upcoming"
            value={upcoming}
            tone="green"
          />
          <StatCard
            testid="stat-guests"
            icon={<Users size={18} />}
            label="Total guests booked"
            value={totalGuests}
            tone="gold"
          />
          <StatCard
            testid="stat-messages"
            icon={<Phone size={18} />}
            label="Messages"
            value={contacts.length}
            tone="dark"
          />
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            testid="stat-subscribers"
            icon={<Users size={18} />}
            label="Subscribers"
            value={subscribers.length}
            tone="gold"
          />
        </div>

        <section className="mt-10 bg-white border border-[#E8E2D6] rounded-3xl overflow-hidden">
          <header className="px-5 sm:px-7 py-5 flex items-center justify-between border-b border-[#E8E2D6]">
            <h2 className="font-display text-xl font-bold">Reservations</h2>
            <span className="text-xs text-[#6B635A]">
              {reservations.length} total
            </span>
          </header>
          {loading ? (
            <p className="px-6 py-10 text-center text-[#6B635A]">Loading…</p>
          ) : reservations.length === 0 ? (
            <p
              data-testid="empty-reservations"
              className="px-6 py-10 text-center text-[#6B635A]"
            >
              No reservations yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FFFCF5] text-[#6B635A] text-xs uppercase tracking-wider">
                  <tr>
                    <Th>Name</Th>
                    <Th>Phone</Th>
                    <Th>Date</Th>
                    <Th>Time</Th>
                    <Th>Party</Th>
                    <Th>Notes</Th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => {
                    const isUpcoming = r.date >= today;
                    return (
                      <tr
                        key={r.id}
                        data-testid={`reservation-row-${r.id}`}
                        className="border-t border-[#E8E2D6] hover:bg-[#FFFCF5]/60"
                      >
                        <Td>
                          <div className="font-semibold">{r.name}</div>
                          {isUpcoming ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#344E41] mt-0.5">
                              <Clock size={11} /> upcoming
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#6B635A] mt-0.5">
                              <CheckCircle2 size={11} /> past
                            </span>
                          )}
                        </Td>
                        <Td>
                          <a
                            href={`tel:${r.phone}`}
                            className="hover:text-[#D9534F]"
                          >
                            {r.phone}
                          </a>
                        </Td>
                        <Td>{r.date}</Td>
                        <Td>{r.time}</Td>
                        <Td>
                          <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-[#FCE9E1] text-[#C73E3A] font-bold text-xs">
                            {r.party_size}
                          </span>
                        </Td>
                        <Td>
                          <span className="text-[#6B635A]">
                            {r.notes || "—"}
                          </span>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="mt-8 bg-white border border-[#E8E2D6] rounded-3xl overflow-hidden">
          <header className="px-5 sm:px-7 py-5 flex items-center justify-between border-b border-[#E8E2D6]">
            <h2 className="font-display text-xl font-bold">Contact messages</h2>
            <span className="text-xs text-[#6B635A]">
              {contacts.length} total
            </span>
          </header>
          {loading ? (
            <p className="px-6 py-10 text-center text-[#6B635A]">Loading…</p>
          ) : contacts.length === 0 ? (
            <p
              data-testid="empty-messages"
              className="px-6 py-10 text-center text-[#6B635A]"
            >
              No messages yet.
            </p>
          ) : (
            <ul className="divide-y divide-[#E8E2D6]">
              {contacts.map((m) => (
                <li
                  key={m.id}
                  data-testid={`message-row-${m.id}`}
                  className="px-5 sm:px-7 py-5"
                >
                  <div className="flex flex-wrap items-baseline gap-2 justify-between">
                    <p className="font-display font-bold">
                      {m.name}{" "}
                      <a
                        href={`mailto:${m.email}`}
                        className="font-normal text-sm text-[#6B635A] hover:text-[#D9534F]"
                      >
                        ({m.email})
                      </a>
                    </p>
                    <span className="text-xs text-[#6B635A]">
                      {new Date(m.created_at).toLocaleString("en-NG")}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#2C2825]/85 leading-relaxed">
                    {m.message}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-8 bg-white border border-[#E8E2D6] rounded-3xl overflow-hidden">
          <header className="px-5 sm:px-7 py-5 flex items-center justify-between border-b border-[#E8E2D6]">
            <div>
              <h2 className="font-display text-xl font-bold">Subscribers</h2>
              <p className="text-xs text-[#6B635A] mt-0.5">
                Customers who opted in before ordering — text them your specials.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => copyAllPhones(subscribers)}
                data-testid="copy-subscriber-phones"
                disabled={!subscribers.length}
                className="text-xs font-semibold text-[#6B635A] hover:text-[#C73E3A] border border-[#E8E2D6] hover:border-[#C73E3A] px-3 py-1.5 rounded-full disabled:opacity-50 transition-colors"
              >
                Copy all phones
              </button>
              <span className="text-xs text-[#6B635A]">
                {subscribers.length} total
              </span>
            </div>
          </header>
          {loading ? (
            <p className="px-6 py-10 text-center text-[#6B635A]">Loading…</p>
          ) : subscribers.length === 0 ? (
            <p
              data-testid="empty-subscribers"
              className="px-6 py-10 text-center text-[#6B635A]"
            >
              No subscribers yet — they'll appear here as customers tap "Order on WhatsApp".
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FFFCF5] text-[#6B635A] text-xs uppercase tracking-wider">
                  <tr>
                    <Th>Name</Th>
                    <Th>Phone</Th>
                    <Th>Email</Th>
                    <Th>Source</Th>
                    <Th>Joined</Th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr
                      key={s.id}
                      data-testid={`subscriber-row-${s.id}`}
                      className="border-t border-[#E8E2D6] hover:bg-[#FFFCF5]/60"
                    >
                      <Td>
                        <div className="font-semibold">{s.name}</div>
                      </Td>
                      <Td>
                        <a
                          href={`https://wa.me/${s.phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#C73E3A]"
                        >
                          {s.phone}
                        </a>
                      </Td>
                      <Td>
                        <span className="text-[#6B635A]">{s.email || "—"}</span>
                      </Td>
                      <Td>
                        <span className="text-[10px] font-semibold text-[#344E41] uppercase tracking-wider">
                          {s.source || "—"}
                        </span>
                      </Td>
                      <Td>
                        <span className="text-[#6B635A]">
                          {new Date(s.created_at).toLocaleDateString("en-NG")}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

async function copyAllPhones(list) {
  const phones = list.map((s) => s.phone).filter(Boolean).join(", ");
  if (!phones) return;
  try {
    await navigator.clipboard.writeText(phones);
    const { toast } = await import("sonner");
    toast.success(`Copied ${list.length} phone numbers`);
  } catch {
    // no-op
  }
}

const TONE = {
  primary: "bg-[#C73E3A] text-white",
  green: "bg-[#344E41] text-white",
  gold: "bg-[#F2B441] text-[#2C2825]",
  dark: "bg-[#2C2825] text-white",
};

function StatCard({ icon, label, value, tone = "primary", testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-3xl p-5 sm:p-6 ${TONE[tone]} flex flex-col justify-between min-h-[120px]`}
    >
      <span className="w-9 h-9 rounded-full bg-black/15 grid place-items-center">
        {icon}
      </span>
      <div className="mt-4">
        <div className="font-display text-3xl sm:text-4xl font-extrabold leading-none">
          {value}
        </div>
        <div className="text-xs uppercase tracking-wider opacity-85 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left font-semibold px-4 sm:px-5 py-3">{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 sm:px-5 py-3 align-top">{children}</td>;
}
